import { PENDING_SPINNER_DELAY_MS } from "../../ui/kit/pending.ts";
import { PENDING_TIMEOUT_MS, type BagTimer, type Waiting } from "./snapshot.ts";
import type { ChangedSlot } from "./wire.ts";

/**
 * The key the sort's timeout and slow mark are kept under (P2.8-T48): a sort names no slot, and no slot has this number.
 */
export const SORT_WAIT = -1;

/**
 * The bag store's bookkeeping of the requests waiting for the server (P2.8-T11, P2.8-T14, P2.8-T41 and P2.8-T48): each
 * slot's timeout, and the sort's under `SORT_WAIT`; the moves, splits and destroys still waiting by the slot each names
 * first; and each move's slow mark, and the sort's. It holds no snapshot: the store asks it which slots a request
 * hatched and whether a move is slow, and changes the snapshot itself.
 *
 * A slow move (P2.8-T41, ADR-091's note of that row): a move that has waited `PENDING_SPINNER_DELAY_MS` with no answer
 * marks the snapshot `slow` while it is the latest move waiting, and whatever ends its wait (its answer, its refusal,
 * a `bag`, the timeout) clears the mark in the same change. A split, a destroy and a use never turn slow.
 */
export class BagWaits {
  private readonly timeouts = new Map<number, () => void>();
  private readonly moving = new Map<number, Waiting>();
  private readonly slowTimers = new Map<number, () => void>();
  private readonly slowMoves = new Set<number>();

  /**
   * @param timer Schedules a timeout and a move's slow mark.
   */
  public constructor(private readonly timer: BagTimer) {}

  /**
   * Keeps a move, a split or a destroy as waiting, by the slot it names first.
   *
   * @param from The slot it names first.
   * @param waiting The request.
   */
  public hold(from: number, waiting: Waiting): void {
    this.moving.set(from, waiting);
  }

  /**
   * Starts a slot's timeout: `PENDING_TIMEOUT_MS` from now, unless its wait ends first, `expired` runs.
   *
   * @param slot The slot.
   * @param expired Run once the time has passed with no answer.
   */
  public timeout(slot: number, expired: () => void): void {
    this.timeouts.set(
      slot,
      this.timer(() => {
        this.timeouts.delete(slot);
        expired();
      }, PENDING_TIMEOUT_MS),
    );
  }

  /**
   * Starts a move's slow mark, or the sort's: `PENDING_SPINNER_DELAY_MS` from now, unless its wait ends first, the
   * request turns slow and `slowed` runs.
   *
   * @param from The slot the move came from, or `SORT_WAIT`.
   * @param slowed Run once the move has turned slow.
   */
  public slowAfter(from: number, slowed: () => void): void {
    this.slowTimers.set(
      from,
      this.timer(() => {
        this.slowTimers.delete(from);
        this.slowMoves.add(from);
        slowed();
      }, PENDING_SPINNER_DELAY_MS),
    );
  }

  /**
   * Takes the request of one kind waiting from a slot, ending its wait.
   *
   * @param from The slot the request named first.
   * @param kind Which request the answer is for.
   * @returns The request, or undefined when none of that kind waits from that slot.
   */
  public take(from: number, kind: Waiting["kind"]): Waiting | undefined {
    const waiting = this.moving.get(from);
    if (waiting === undefined || waiting.kind !== kind) {
      return undefined;
    }
    this.moving.delete(from);
    this.end(from);
    return waiting;
  }

  /**
   * Cancels a slot's timeout and its move's slow mark, if either runs.
   *
   * @param slot The slot.
   */
  public end(slot: number): void {
    this.timeouts.get(slot)?.();
    this.timeouts.delete(slot);
    this.slowTimers.get(slot)?.();
    this.slowTimers.delete(slot);
    this.slowMoves.delete(slot);
  }

  /**
   * Cancels every slot's timeout and every move's slow mark, and forgets every request waiting.
   */
  public endAll(): void {
    for (const cancel of [...this.timeouts.values(), ...this.slowTimers.values()]) {
      cancel();
    }
    this.timeouts.clear();
    this.slowTimers.clear();
    this.slowMoves.clear();
    this.moving.clear();
  }

  /**
   * Whether a move, a split or a destroy still waiting names a slot, whose hatch only that request's answer clears.
   *
   * @param slot The slot.
   * @returns True while one does.
   */
  public inMove(slot: number): boolean {
    for (const [from, { other }] of this.moving) {
      if (from === slot || other === slot) {
        return true;
      }
    }
    return false;
  }

  /**
   * The snapshot's marks of the moves waiting: the slot the latest came from, and whether that move is slow.
   *
   * @returns `moving` and `slow`.
   */
  public marks(): { readonly moving: number | null; readonly slow: boolean } {
    let moving: number | null = null;
    for (const [from, { kind }] of this.moving) {
      if (kind === "move") {
        moving = from;
      }
    }
    return { moving, slow: moving !== null && this.slowMoves.has(moving) };
  }

  /**
   * Forgets the requests of one kind a change of that cause answered: each whose slots the change names. A split's new
   * half may land elsewhere than the page foresaw (loot took that slot first), so the slots each answered request
   * hatched are returned for the caller to clear, the ones the change names among them.
   *
   * @param changed The slots the change names.
   * @param cause The change's cause: `move`, `split`, `destroy` or `drop`.
   * @returns The slots the answered requests had hatched.
   */
  public forget(changed: readonly ChangedSlot[], cause: Waiting["kind"]): number[] {
    const answered: number[] = [];
    for (const [from, { kind, other }] of [...this.moving]) {
      if (kind === cause && changed.some(([slot]) => slot === from || slot === other)) {
        this.moving.delete(from);
        this.end(from);
        answered.push(from, other);
      }
    }
    return answered;
  }
}
